import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { exportJobCsv, exportProfileCsv, ExportMode } from '../../utils/export';
import { normalizeJobResult } from '../../utils/results';

type JobRow = {
  id: string;
  user_id: string | null;
  category_set_id: string | null;
  result: { text: string; category: string }[] | null;
  status: string | null;
  created_at: string | null;
};

type Profile = {
  id: string;
  name: string;
  mode: 'static' | 'dynamic';
  categories: string[] | null;
  category_details: { name: string; description?: string }[] | null;
  created_at: string | null;
};

function PieChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let angle = 0;
  const radius = 56;
  const cx = 64;
  const cy = 64;
  return (
    <svg width={128} height={128} viewBox="0 0 128 128">
      {data.map((d, i) => {
        const startAngle = angle;
        const sliceAngle = (d.value / total) * Math.PI * 2;
        angle += sliceAngle;
        const endAngle = angle;
        const x1 = cx + radius * Math.cos(startAngle);
        const y1 = cy + radius * Math.sin(startAngle);
        const x2 = cx + radius * Math.cos(endAngle);
        const y2 = cy + radius * Math.sin(endAngle);
        const largeArc = sliceAngle > Math.PI ? 1 : 0;
        const pathData = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        return <path key={i} d={pathData} fill={d.color} stroke="rgba(255,255,255,0.08)" />;
      })}
    </svg>
  );
}

const colors = ['#5465FF', '#92DCE5', '#788BFF', '#E9EB9E', '#9be564', '#ff6b6b', '#c084fc'];

const ProfileDashboard: React.FC<{ profileId: string }> = ({ profileId }) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // Ensure user context for RLS
        const { data: u } = await supabase.auth.getUser();
        const uid = u.user?.id;
        if (!uid) {
          setError('Please log in to view this profile.');
          return;
        }

        const { data: prof, error: pErr } = await supabase
          .from('category_sets')
          .select('id, name, mode, categories, category_details, created_at')
          .eq('id', profileId)
          .single();
        if (pErr) throw pErr;
        setProfile(prof as any);

        const { data: js, error: jErr } = await supabase
          .from('jobs')
          .select('id, user_id, category_set_id, result, status, created_at')
          .eq('category_set_id', profileId)
          .eq('user_id', uid)
          .order('created_at', { ascending: false });
        if (jErr) throw jErr;
        setJobs((js as any) || []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load profile dashboard');
      }
    })();
  }, [profileId]);

  const { totals, processedItems, jobsCount } = useMemo(() => {
    const catMap = new Map<string, number>();
    let processed = 0;
    for (const j of jobs) {
      const arr = normalizeJobResult(j.result);
      processed += arr.length;
      for (const r of arr) {
        catMap.set(r.category, (catMap.get(r.category) || 0) + 1);
      }
    }
    const entries = Array.from(catMap.entries()).sort((a, b) => b[1] - a[1]);
    return {
      totals: entries,
      processedItems: processed,
      jobsCount: jobs.length,
    };
  }, [jobs]);

  const pieData = useMemo(() => {
    const items = totals.map(([label, value], idx) => ({ label, value, color: colors[idx % colors.length] }));
    return items.length > 0 ? items : [{ label: 'No data', value: 1, color: 'rgba(255,255,255,0.08)' }];
  }, [totals]);

  const handleExportProfile = (mode: ExportMode) => {
    const allResults = jobs.flatMap(j => normalizeJobResult(j.result));
    const cats = profile?.categories || profile?.category_details?.map(d => d.name) || [];
    exportProfileCsv(profileId, allResults, cats, mode);
  };

  const handleExportJob = (job: JobRow, mode: ExportMode) => {
    const results = normalizeJobResult(job.result);
    const cats = profile?.categories || profile?.category_details?.map(d => d.name) || [];
    exportJobCsv(job.id, results, cats, mode);
  };

  if (error) return <div style={{ color: '#ff6b6b' }}>{error}</div>;
  if (!profile) return <div>Loading…</div>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ margin: 0 }}>{profile.name} <span style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>({profile.mode})</span></h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>
        <div className="card" style={{ padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Distribution</h4>
          <PieChart data={pieData} />
          <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
            {pieData.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, display: 'inline-block' }} />
                <span>{d.label}</span>
                <span style={{ marginLeft: 'auto', color: 'var(--color-text-muted)' }}>{d.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Overview</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <Stat title="Jobs" value={String(jobsCount)} />
            <Stat title="Processed items" value={String(processedItems)} />
            <Stat title="Categories" value={String((profile.categories || profile.category_details || []).length)} />
            <Stat title="Most common" value={totals[0]?.[0] || '—'} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="button" onClick={() => handleExportProfile('single')}>Export CSV</button>
            <button className="button secondary" onClick={() => handleExportProfile('onehot')}>Export one-hot CSV</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginTop: 0 }}>Jobs</h4>
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f111a' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Number</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Processed items</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Creation date</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j, idx) => (
                <tr key={j.id}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{idx + 1}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{(j.result || []).length}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{j.status || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{j.created_at ? new Date(j.created_at).toLocaleString() : '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>
                    <button className="button secondary" onClick={() => window.location.href = `/app/job/${j.id}`}>Dashboard</button>
                    <button className="button" style={{ marginLeft: 8 }} onClick={() => handleExportJob(j, 'single')}>Export CSV</button>
                    <button className="button secondary" style={{ marginLeft: 8 }} onClick={() => handleExportJob(j, 'onehot')}>Export one-hot</button>
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '12px', textAlign: 'center', color: 'var(--color-text-muted)' }}>No jobs yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginTop: 0 }}>Categories</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(profile.categories || profile.category_details?.map(d => d.name) || []).map((c, i) => (
            <span key={i} style={{ padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 999 }}>{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

function Stat({ title, value }: { title: string; value: string }) {
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, background: 'var(--color-surface)' }}>
      <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

export default ProfileDashboard; 